import React, { useEffect, useState, createContext, useContext } from "react";
import PropTypes from "prop-types";
import { FlaskConical, AlertTriangle, X } from "lucide-react";
import { fetchHealth, isBackendUnavailable, isAuthExpired } from "../../services/LabService";
import LabUpgradeSplash from "./LabUpgradeSplash";

/**
 * The backend has no /api/lab/access endpoint. Per the backend note §3.1
 * we probe /api/lab/health: a 200 means the JWT and tier are both fine,
 * a 403 means the tier is too low, and any other failure means the
 * router isn't deployed yet → preview mode.
 *
 * The resolved health payload is exposed via LabHealthContext so pages
 * can read pipeline + endpoint state without re-fetching.
 */

const LabHealthContext = createContext(null);
export const useLabHealth = () => useContext(LabHealthContext);
// Alias for v1 compatibility — existing pages call useLabAccess()
export const useLabAccess = () => useContext(LabHealthContext);

const PREVIEW_HEALTH = {
    ok: true,
    endpoint: null,
    pipeline: null,
    _preview: true,
};

const DISMISS_KEY = "lab_preview_banner_dismissed";

const PreviewBanner = () => {
    const [dismissed, setDismissed] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.sessionStorage.getItem(DISMISS_KEY) === "1";
    });
    if (dismissed) return null;
    const dismiss = () => {
        try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch (_) { /* private mode */ }
        setDismissed(true);
    };
    return (
        <div className="bg-amber-50/70 dark:bg-amber-950/20 border-b border-amber-200/60 dark:border-amber-900/60 px-4 py-1.5 text-[11px] text-amber-800 dark:text-amber-200 flex items-center gap-2">
            <AlertTriangle className="w-3 h-3 flex-shrink-0" />
            <span className="flex-1">
                Preview mode — <code className="font-mono">/api/lab/*</code> not reachable. UI populates once backend ships.
            </span>
            <button
                onClick={dismiss}
                aria-label="Dismiss preview banner"
                className="text-amber-700/70 hover:text-amber-900 dark:text-amber-300/70 dark:hover:text-amber-100 p-0.5 rounded"
            >
                <X className="w-3 h-3" />
            </button>
        </div>
    );
};

const LabAccessGate = ({ children, forceUpgradeView }) => {
    const [state, setState] = useState({ loading: true, health: null, error: null });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const health = await fetchHealth();
                if (!cancelled) setState({ loading: false, health, error: null });
            } catch (error) {
                if (!cancelled) setState({ loading: false, health: null, error });
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (state.loading) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center">
                <div className="flex items-center gap-3 text-slate-500">
                    <FlaskConical className="w-5 h-5 animate-pulse" />
                    <span>Loading the Lab…</span>
                </div>
            </div>
        );
    }

    if (state.error && state.error.status === 403) {
        return <LabUpgradeSplash currentTier={state.error.body?.tier || null} />;
    }

    // Auth expired or invalid → either the global auth handler is already
    // redirecting (configured in App.js), or we show a clean "sign in again"
    // message here. Don't fall through to preview mode (the routes ARE
    // deployed; the user just isn't logged in correctly).
    if (state.error && isAuthExpired(state.error)) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">
                        Sign in to access the Lab
                    </h2>
                    <p className="text-sm text-slate-500 mb-4">
                        Your session has expired. Sign in again to continue.
                    </p>
                    <button
                        onClick={() => { window.location.href = "/login"; }}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                    >
                        Sign in
                    </button>
                </div>
            </div>
        );
    }

    if (state.error && isBackendUnavailable(state.error)) {
        return (
            <LabHealthContext.Provider value={PREVIEW_HEALTH}>
                <PreviewBanner />
                {children}
            </LabHealthContext.Provider>
        );
    }

    if (state.error) {
        return (
            <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex items-center justify-center px-6">
                <div className="max-w-md text-center">
                    <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-50 mb-2">
                        We couldn't load the Lab
                    </h2>
                    <p className="text-sm text-slate-500 mb-4">{state.error.message}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"
                    >
                        Try again
                    </button>
                </div>
            </div>
        );
    }

    if (forceUpgradeView) {
        return <LabUpgradeSplash />;
    }

    return (
        <LabHealthContext.Provider value={state.health}>
            {children}
        </LabHealthContext.Provider>
    );
};

LabAccessGate.propTypes = {
    children: PropTypes.node.isRequired,
    forceUpgradeView: PropTypes.bool,
};
LabAccessGate.defaultProps = { forceUpgradeView: false };

export default LabAccessGate;
